import React, { useState, useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../api/api';
import ReferenceInfoDialog from '../components/ReferenceInfoDialog';

/* ─── constants ─── */
const SERIES_OPTS    = ['Primary'];
const PRICE_LISTS    = ['Price List 01', 'Last Evaluated Price', 'Average Price', 'Standard Price'];
const STATUS_OPTS    = ['Draft', 'Open', 'Completed', 'Cancelled'];
const STATUS_COLORS  = {
  Draft:     'bg-[#F0F4F3] text-[#6B7F7F]',
  Open:      'bg-[#EDF5F0] text-[#4A9E6E]',
  Completed: 'bg-[#EDF2F7] text-[#4A6FA5]',
  Cancelled: 'bg-[#FDF0EE] text-[#C4574B]',
};
const today = () => new Date().toISOString().slice(0, 10);
const INV_ITEM_PAGE_SIZE = 50;

const blankLine = () => ({
  _key: Date.now() + Math.random(),
  item_id: '', item_no: '', item_description: '',
  project_code: '', project_name: '',
  from_warehouse_id: '', from_wh_code: '',
  to_warehouse_id: '', to_wh_code: '',
  quantity: '1.0000', item_cost: '', uom_code: '', uom_name: '',
  product_group: '', period: '', requester: '', request_ref: '', report: '',
});

const blankDoc = {
  transfer_number: '', series_id: '', series: 'Primary',
  posting_date: today(), document_date: today(),
  referenced_document: '',
  project_code: '', project_name: '',
  bp_code: '', bp_name: '', contact_person: '', ship_to: '',
  from_warehouse_id: '', from_wh_code: '',
  to_warehouse_id: '', to_wh_code: '',
  price_list: 'Price List 01',
  sales_employee: '', journal_remarks: 'Inventory Transfers -', remarks: '',
  status: 'Draft',
};

/** Mirrors header warehouses + project onto a line row (SAP-style: entered once in header). */
const headerFieldsForLines = (f) => ({
  from_warehouse_id: f.from_warehouse_id || '',
  from_wh_code: f.from_wh_code || '',
  to_warehouse_id: f.to_warehouse_id || '',
  to_wh_code: f.to_wh_code || '',
  project_code: f.project_code || '',
  project_name: f.project_name || '',
});

const fmt4 = (v) => (isNaN(+v) ? '0.0000' : Number(v).toFixed(4));

/* ─────────────── YellowArrow ─────────────── */
const YellowArrow = ({ onClick, title }) => (
  <button type="button" onClick={onClick} title={title}
    className="flex-shrink-0 flex items-center justify-center w-5 h-5 hover:opacity-80 transition-opacity">
    <svg width="12" height="12" viewBox="0 0 14 14" className="drop-shadow-sm">
      <polygon points="1,1 13,7 1,13" fill="#E8A000" stroke="#C47800" strokeWidth="0.8" strokeLinejoin="round" />
    </svg>
  </button>
);

/* ─────────────── WarehousePickerDialog ─────────────── */
const WarehousePickerDialog = ({ warehouses, currentWhId, label, onSelect, onClose }) => {
  const [search, setSearch] = React.useState('');
  const filtered = warehouses.filter(w =>
    !search ||
    w.wh_code.toLowerCase().includes(search.toLowerCase()) ||
    w.wh_name.toLowerCase().includes(search.toLowerCase())
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl border border-sap-border w-[600px] max-h-[480px] flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2.5 bg-sap-blue rounded-t-xl">
          <span className="text-sap-text text-xs font-semibold">Select Warehouse{label ? ` — ${label}` : ''}</span>
          <button onClick={onClose} className="text-sap-textLight hover:text-sap-text text-base leading-none">✕</button>
        </div>
        <div className="px-4 py-2 border-b border-sap-border">
          <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by code or name…"
            className="w-full h-7 px-2 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue" />
        </div>
        <div className="overflow-y-auto flex-1">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-sap-blue/5 border-b border-sap-border/40">
              <tr>
                <th className="text-left px-3 py-1.5 text-[10px] font-semibold text-sap-textLight uppercase tracking-wider w-16">Code</th>
                <th className="text-left px-3 py-1.5 text-[10px] font-semibold text-sap-textLight uppercase tracking-wider">Warehouse Name</th>
                <th className="text-right px-3 py-1.5 text-[10px] font-semibold text-sap-textLight uppercase tracking-wider w-20">In Stock</th>
                <th className="text-right px-3 py-1.5 text-[10px] font-semibold text-sap-textLight uppercase tracking-wider w-20">Committed</th>
                <th className="text-right px-3 py-1.5 text-[10px] font-semibold text-sap-textLight uppercase tracking-wider w-20">Available</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(w => {
                const isCurrent = String(w.wh_id) === String(currentWhId);
                return (
                  <tr key={w.wh_id} onClick={() => onSelect(w)}
                    className={`border-t border-sap-border/20 cursor-pointer transition-colors
                      ${isCurrent ? 'bg-sap-blue/10 font-semibold' : 'hover:bg-sap-blue/5'}`}>
                    <td className="px-3 py-1.5 text-sap-blue font-mono font-semibold">{w.wh_code}</td>
                    <td className="px-3 py-1.5 text-sap-text">{w.wh_name}</td>
                    <td className="px-3 py-1.5 text-right text-sap-textLight">{w.in_stock != null ? Number(w.in_stock).toFixed(2) : '—'}</td>
                    <td className="px-3 py-1.5 text-right text-sap-textLight">{w.committed != null ? Number(w.committed).toFixed(2) : '—'}</td>
                    <td className={`px-3 py-1.5 text-right font-semibold ${Number(w.available) > 0 ? 'text-sap-blueDeeper' : 'text-sap-textLight'}`}>
                      {w.available != null ? Number(w.available).toFixed(2) : '—'}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-4 text-center text-sap-textLight">No warehouses found</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 border-t border-sap-border bg-gray-50 rounded-b-xl text-[10px] text-sap-textLight">
          Click a row to select • {filtered.length} warehouse{filtered.length !== 1 ? 's' : ''}
        </div>
      </div>
    </div>
  );
};

/* ─────────────── ProjectPickerDialog ─────────────── */
const ProjectPickerDialog = ({ onSelect, onClose }) => {
  const [search, setSearch] = React.useState('');
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      api.get(`/projects?search=${encodeURIComponent(search)}`)
        .then((d) => setRows(Array.isArray(d) ? d : []))
        .catch(() => setRows([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl border border-sap-border w-[620px] max-h-[460px] flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2.5 bg-sap-blue rounded-t-xl">
          <span className="text-sap-text text-xs font-semibold">Select Project</span>
          <button type="button" onClick={onClose} className="text-sap-textLight hover:text-sap-text text-base leading-none">✕</button>
        </div>
        <div className="px-4 py-2 border-b border-sap-border">
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search code or name…"
            className="w-full h-7 px-2 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue"
          />
        </div>
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="py-8 text-center text-xs text-sap-textLight">Loading…</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-sap-blue/5 border-b border-sap-border/40">
                <tr>
                  <th className="text-left px-3 py-1.5 text-[10px] font-semibold text-sap-textLight uppercase tracking-wider w-40">
                    Project Code
                  </th>
                  <th className="text-left px-3 py-1.5 text-[10px] font-semibold text-sap-textLight uppercase tracking-wider">
                    Project Name
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr
                    key={p.project_code}
                    onClick={() => onSelect(p)}
                    className="border-t border-sap-border/20 hover:bg-sap-blue/5 cursor-pointer transition-colors"
                  >
                    <td className="px-3 py-1.5 text-sap-blue font-mono font-semibold">{p.project_code}</td>
                    <td className="px-3 py-1.5 text-sap-text">{p.project_name || '—'}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={2} className="px-3 py-4 text-center text-sap-textLight">
                      No projects found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        <div className="px-4 py-2 border-t border-sap-border bg-gray-50 rounded-b-xl text-[10px] text-sap-textLight">
          Double-click a row or click to select
        </div>
      </div>
    </div>
  );
};

/* ─────────────── CopyFromDialog ─────────────── */
const CopyFromDialog = ({ onSelect, onClose }) => {
  const [rows, setRows]     = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedIds, setSelectedIds] = React.useState(new Set());

  useEffect(() => {
    api.get('/inventory-transfer-requests').then(d => { setRows(d || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const open = rows.filter(r => r.status === 'Open' || r.status === 'Draft');
  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selected = open.filter(r => selectedIds.has(r.request_id));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl border border-sap-border w-[700px] max-h-[500px] flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2.5 bg-sap-blue rounded-t-xl">
          <span className="text-sap-text text-xs font-semibold">Copy From — Inventory Transfer Requests</span>
          <button onClick={onClose} className="text-sap-textLight hover:text-sap-text text-base leading-none">✕</button>
        </div>
        <div className="overflow-y-auto flex-1">
          {loading ? <div className="py-6 text-center text-xs text-sap-textLight">Loading…</div> : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-sap-blue/5 border-b border-sap-border/40">
                <tr>
                  <th className="text-center px-3 py-1.5 text-[10px] font-semibold text-sap-textLight uppercase tracking-wider w-10">Sel</th>
                  <th className="text-left px-3 py-1.5 text-[10px] font-semibold text-sap-textLight uppercase tracking-wider">Request No.</th>
                  <th className="text-left px-3 py-1.5 text-[10px] font-semibold text-sap-textLight uppercase tracking-wider">From Wh</th>
                  <th className="text-left px-3 py-1.5 text-[10px] font-semibold text-sap-textLight uppercase tracking-wider">To Wh</th>
                  <th className="text-left px-3 py-1.5 text-[10px] font-semibold text-sap-textLight uppercase tracking-wider">Due Date</th>
                  <th className="text-left px-3 py-1.5 text-[10px] font-semibold text-sap-textLight uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody>
                {open.map(r => (
                  <tr key={r.request_id} onClick={() => toggle(r.request_id)}
                    className={`border-t border-sap-border/20 cursor-pointer transition-colors hover:bg-sap-blue/5
                      ${selectedIds.has(r.request_id) ? 'bg-sap-blue/10' : ''}`}>
                    <td className="px-3 py-1.5 text-center">
                      <input type="checkbox" className="accent-sap-blue"
                        checked={selectedIds.has(r.request_id)}
                        onChange={() => toggle(r.request_id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td className="px-3 py-1.5 text-sap-blue font-semibold">{r.request_number}</td>
                    <td className="px-3 py-1.5 font-mono text-sap-text">{r.from_wh_code || '—'}</td>
                    <td className="px-3 py-1.5 font-mono text-sap-text">{r.to_wh_code || '—'}</td>
                    <td className="px-3 py-1.5 text-sap-textLight">{r.due_date ? r.due_date.slice(0, 10) : '—'}</td>
                    <td className="px-3 py-1.5 text-sap-textLight">{r.status}</td>
                  </tr>
                ))}
                {open.length === 0 && !loading && (
                  <tr><td colSpan={6} className="px-3 py-4 text-center text-sap-textLight">No open transfers found</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        <div className="px-4 py-2 border-t border-sap-border bg-gray-50 rounded-b-xl flex items-center justify-between gap-2">
          <div className="text-[10px] text-sap-textLight">
            Select one or more requests, then click “Add Selected”.
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose}
              className="px-3 py-1.5 text-xs border border-sap-border rounded hover:bg-white transition-colors">
              Cancel
            </button>
            <button type="button"
              disabled={selected.length === 0}
              onClick={() => onSelect(selected)}
              className="px-3 py-1.5 text-xs bg-sap-blue text-white rounded hover:bg-sap-blueDark disabled:opacity-50 transition-colors font-semibold">
              Add Selected ({selected.length})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────── SalesEmployeePickerDialog ─────────────── */
const SalesEmployeePickerDialog = ({ current, onSelect, onClose }) => {
  const [search, setSearch] = React.useState('');
  const [employees, setEmployees] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [highlighted, setHighlighted] = React.useState(current || '');

  useEffect(() => {
    api.get('/sales-employees')
      .then(d => { setEmployees(d || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filtered = employees.filter(e =>
    !search || e.employee_name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl border border-sap-border w-[520px] max-h-[480px] flex flex-col"
        onClick={e => e.stopPropagation()}>
        {/* Title bar */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-sap-blue rounded-t-xl">
          <span className="text-sap-text text-xs font-semibold">List of Sales Employee</span>
          <button onClick={onClose} className="text-sap-textLight hover:text-sap-text text-base leading-none">✕</button>
        </div>
        {/* Search bar */}
        <div className="px-4 py-2 border-b border-sap-border">
          <div className="flex items-center gap-2">
            <span className="text-xs text-sap-text whitespace-nowrap font-medium">Find</span>
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
              className="flex-1 h-7 px-2 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue" />
          </div>
        </div>
        {/* Table */}
        <div className="overflow-y-auto flex-1">
          {loading ? <div className="py-6 text-center text-xs text-sap-textLight">Loading…</div> : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-sap-blue/5 border-b border-sap-border/40">
                <tr>
                  <th className="text-left px-3 py-1.5 text-[10px] font-semibold text-sap-textLight uppercase tracking-wider w-10">#</th>
                  <th className="text-left px-3 py-1.5 text-[10px] font-semibold text-sap-textLight uppercase tracking-wider">Sales Employee Name</th>
                  <th className="text-left px-3 py-1.5 text-[10px] font-semibold text-sap-textLight uppercase tracking-wider w-40">Remarks</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((emp, idx) => {
                  const isHL = emp.employee_name === highlighted;
                  return (
                    <tr key={emp.id}
                      onClick={() => setHighlighted(emp.employee_name)}
                      onDoubleClick={() => { onSelect(emp.employee_name); onClose(); }}
                      className={`cursor-pointer border-t border-sap-border/20 transition-colors ${
                        isHL ? 'bg-sap-blue/10 font-semibold' : 'hover:bg-sap-blue/5'
                      }`}>
                      <td className="px-3 py-1.5 text-sap-textLight">{idx + 1}</td>
                      <td className="px-3 py-1.5 text-sap-text">{emp.employee_name}</td>
                      <td className="px-3 py-1.5 text-sap-textLight">{emp.remarks || ''}</td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={3} className="px-3 py-4 text-center text-sap-textLight">No results</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        {/* Buttons */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-t border-sap-border bg-gray-50 rounded-b-xl">
          <button
            onClick={() => { if (highlighted) { onSelect(highlighted); onClose(); } }}
            className="px-5 py-1.5 text-xs bg-sap-blue text-white rounded-lg font-semibold hover:bg-sap-blueDark transition-colors">
            Choose
          </button>
          <button onClick={onClose}
            className="px-4 py-1.5 text-xs border border-sap-border rounded-lg text-sap-text hover:bg-gray-50 font-semibold transition-colors">
            Cancel
          </button>
          <span className="ml-auto text-[10px] text-sap-textLight">{filtered.length} employee{filtered.length !== 1 ? 's' : ''}</span>
        </div>
      </div>
    </div>
  );
};

/* ─── input styles ─── */
const inp = `w-full h-[28px] px-2 text-xs border border-sap-border rounded bg-white focus:outline-none focus:ring-1 focus:ring-sap-blue/40 focus:border-sap-blue`;
const sel = `${inp} appearance-none`;
const lbl = 'block text-[10px] font-medium text-sap-textLight mb-0.5 whitespace-nowrap';
const dis = `w-full h-[28px] px-2 text-xs border border-sap-border rounded bg-gray-100 text-sap-textLight cursor-not-allowed`;

/* ════════════════════════════════════════
   InventoryTransferDetail
════════════════════════════════════════ */
export default function InventoryTransferDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = id === 'new';

  const [tab, setTab]             = useState('Contents');
  const [attachments, setAttachments] = useState([]); // { id, targetPath, fileName, attachmentDate, freeText, copyToTarget, file }
  const [selectedAttIdx, setSelectedAttIdx] = useState(null);
  const attInputRef = React.useRef(null);
  const [form, setForm]           = useState({ ...blankDoc });
  const [lines, setLines]         = useState([blankLine()]);
  const [warehouses, setWarehouses] = useState([]);
  const [itemWarehousesMap, setItemWarehousesMap] = useState({});
  const [loading, setLoading]     = useState(!isNew);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Picker states
  const [whPicker, setWhPicker]   = useState(null);  // { field: 'from'|'to' }
  const [showProjectPicker, setShowProjectPicker] = useState(false);
  const [showSalesPicker, setShowSalesPicker] = useState(false);
  const [showCopyFrom, setShowCopyFrom]   = useState(false);
  const [showRefInfo, setShowRefInfo]       = useState(false);
  const [successPopup, setSuccessPopup]   = useState(null); // { transferNumber, postingDate, status, fromWh, toWh, lines[] }
  const [refsTo, setRefsTo] = useState([]);
  const [refsBy, setRefsBy] = useState([]);
  const [copyFromPending, setCopyFromPending] = useState(false);
  const [salesEmpHighlight, setSalesEmpHighlight] = useState(false);
  const salesEmpRef = React.useRef(null);
  const invItemAnchorRef = useRef(null);
  const invItemLoadMoreRef = useRef(false);
  const [invItemPick, setInvItemPick] = useState({ row: null, q: '' });
  const [invItemLayer, setInvItemLayer] = useState(null);
  const [invItemOptions, setInvItemOptions] = useState([]);
  const [invItemLoading, setInvItemLoading] = useState(false);
  const [invItemLoadingMore, setInvItemLoadingMore] = useState(false);
  const [invItemPage, setInvItemPage] = useState(1);
  const [invItemHasMore, setInvItemHasMore] = useState(false);
  const [invItemTotal, setInvItemTotal] = useState(0);

  const [seriesList, setSeriesList] = useState([]);
  const [selectedSeriesId, setSelectedSeriesId] = useState(null);

  /* ── load warehouses and series ── */
  useEffect(() => {
    api.get('/inventory-transfers/warehouses').then(d => setWarehouses(d || [])).catch(() => {});
    api.get('/inventory-transfers/series')
      .then(list => {
        setSeriesList(list || []);
        if (isNew && list?.length) {
          const def = list.find(s => s.is_default) || list[0];
          setSelectedSeriesId(def.series_id);
          setForm(f => ({ ...f, series: def.series_name, series_id: def.series_id }));
          api.get(`/inventory-transfers/next-number?series_id=${def.series_id}`)
            .then(r => setForm(f => ({ ...f, transfer_number: r?.next || '' })))
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, [isNew]);

  /* handle series change */
  const handleSeriesChange = (seriesId) => {
    const sid = parseInt(seriesId, 10);
    const chosen = seriesList.find(s => s.series_id === sid);
    if (!chosen) return;
    setSelectedSeriesId(sid);
    setForm(f => ({ ...f, series: chosen.series_name, series_id: sid }));
    if (isNew) {
      api.get(`/inventory-transfers/next-number?series_id=${sid}`)
        .then(r => setForm(f => ({ ...f, transfer_number: r?.next || '' })))
        .catch(() => {});
    }
  };

  /* ── load existing doc ── */
  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    api.get(`/inventory-transfers/${id}`)
      .then(d => {
        const { lines: rawLines, ...doc } = d;
        setForm({
          ...blankDoc,
          ...doc,
          posting_date: doc.posting_date ? doc.posting_date.slice(0, 10) : today(),
          document_date: doc.document_date ? doc.document_date.slice(0, 10) : today(),
          project_code: doc.project_code || '',
          project_name: doc.project_name || '',
          from_warehouse_id: doc.from_warehouse_id || '',
          from_wh_code: doc.from_wh_code || '',
          to_warehouse_id: doc.to_warehouse_id || '',
          to_wh_code: doc.to_wh_code || '',
        });
        if (rawLines && rawLines.length > 0) {
          setLines(rawLines.map(l => ({
            ...blankLine(),
            ...l,
            project_code: l.project_code || doc.project_code || '',
            project_name: l.project_name || doc.project_name || '',
            from_warehouse_id: doc.from_warehouse_id || l.from_warehouse_id || '',
            from_wh_code: doc.from_wh_code || l.from_wh_code || '',
            to_warehouse_id: doc.to_warehouse_id || l.to_warehouse_id || '',
            to_wh_code: doc.to_wh_code || l.to_wh_code || '',
          })));
          // Pre-load item warehouses
          const ids = [...new Set(rawLines.map(l => l.item_id).filter(Boolean))];
          ids.forEach(item_id => loadItemWarehouses(item_id));
        } else {
          setLines([blankLine()]);
        }
        setLoading(false);
      })
      .catch(() => { setError('Failed to load transfer'); setLoading(false); });
  }, [id, isNew]); // eslint-disable-line

  /* ── auto copy-from when navigated via Copy To (ITR → IT) ── */
  useEffect(() => {
    if (!isNew) return;
    const params = new URLSearchParams(location.search);
    if (params.get('copyFrom') !== 'itr') return;
    const itrId = parseInt(params.get('id'));
    if (!itrId) return;
    (async () => {
      try {
        const data = await api.get(`/inventory-transfer-requests/${itrId}`);
        if (data.lines && data.lines.length > 0) {
          setLines(data.lines.map((l) => ({
            ...blankLine(),
            item_id: l.item_id || '',
            item_no: l.item_no || '',
            item_description: l.item_description || '',
            project_code: data.project_code || '',
            project_name: data.project_name || '',
            from_warehouse_id: l.from_warehouse_id || data.from_warehouse_id || '',
            from_wh_code: l.from_wh_code || data.from_wh_code || '',
            to_warehouse_id: l.to_warehouse_id || data.to_warehouse_id || '',
            to_wh_code: l.to_wh_code || data.to_wh_code || '',
            quantity: l.quantity || '1.0000',
            item_cost: '',
            uom_code: l.uom_code || '',
            uom_name: l.uom_name || '',
            product_group: l.product_group || '',
            period: l.period || '',
            requester: l.requester || '',
            request_ref: l.request_ref || '',
            report: l.report || '',
          })));
        }
        setForm((f) => ({
          ...f,
          bp_code: data.bp_code || f.bp_code,
          bp_name: data.bp_name || f.bp_name,
          contact_person: data.contact_person || f.contact_person,
          ship_to: data.ship_to || f.ship_to,
          project_code: data.project_code || '',
          project_name: data.project_name || '',
          from_warehouse_id: data.from_warehouse_id ? String(data.from_warehouse_id) : f.from_warehouse_id,
          from_wh_code: data.from_wh_code || f.from_wh_code,
          to_warehouse_id: data.to_warehouse_id ? String(data.to_warehouse_id) : f.to_warehouse_id,
          to_wh_code: data.to_wh_code || f.to_wh_code,
          price_list: data.price_list || f.price_list,
          sales_employee: data.sales_employee || f.sales_employee,
          journal_remarks: 'Inventory Transfer',
          remarks: data.remarks || f.remarks,
        }));
      } catch { setError('Failed to copy from request'); }
    })();
  }, [isNew, location.search]); // eslint-disable-line

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  /** Update header warehouses or project and push the same values to every line. */
  const setHeaderAndSyncLines = React.useCallback((patch) => {
    setForm((f) => {
      const next = typeof patch === 'function' ? patch(f) : { ...f, ...patch };
      setLines((ls) =>
        ls.map((l) => ({
          ...l,
          ...headerFieldsForLines(next),
        }))
      );
      return next;
    });
  }, []);

  const updateLine = (idx, k, v) => setLines(ls => ls.map((l, i) => i === idx ? { ...l, [k]: v } : l));
  const addLine    = () => {
    setForm((f) => {
      setLines((ls) => [...ls, { ...blankLine(), ...headerFieldsForLines(f) }]);
      return f;
    });
  };
  const removeLine = (idx) => setLines(ls => ls.filter((_, i) => i !== idx));

  /* ── load item warehouses cache ── */
  const loadItemWarehouses = async (item_id) => {
    if (!item_id || itemWarehousesMap[item_id]) return;
    try {
      const rows = await api.get(`/inventory-transfers/item-warehouses/${item_id}`);
      setItemWarehousesMap(m => ({ ...m, [item_id]: rows || [] }));
    } catch { /* silent */ }
  };

  const syncInvItemLayer = useCallback(() => {
    if (invItemPick.row === null) {
      setInvItemLayer(null);
      return;
    }
    const el = invItemAnchorRef.current;
    if (!el || typeof el.getBoundingClientRect !== 'function') return;
    const r = el.getBoundingClientRect();
    const gap = 4;
    const maxH = Math.min(
      Math.floor(window.innerHeight * 0.55),
      Math.max(140, window.innerHeight - r.bottom - gap - 12)
    );
    setInvItemLayer({
      top: r.bottom + gap,
      left: r.left,
      width: Math.max(320, r.width),
      maxHeight: maxH,
    });
  }, [invItemPick.row]);

  useLayoutEffect(() => {
    syncInvItemLayer();
  }, [syncInvItemLayer, invItemPick.q, invItemPick.row, lines.length]);

  useEffect(() => {
    if (invItemPick.row === null) return;
    const onWin = () => syncInvItemLayer();
    window.addEventListener('resize', onWin);
    window.addEventListener('scroll', onWin, true);
    return () => {
      window.removeEventListener('resize', onWin);
      window.removeEventListener('scroll', onWin, true);
    };
  }, [invItemPick.row, syncInvItemLayer]);

  useEffect(() => {
    if (invItemPick.row === null) {
      setInvItemOptions([]);
      setInvItemPage(1);
      setInvItemHasMore(false);
      setInvItemTotal(0);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setInvItemLoading(true);
      try {
        const params = new URLSearchParams({
          search: invItemPick.q,
          page: '1',
          pageSize: String(INV_ITEM_PAGE_SIZE),
        });
        const data = await api.get(`/inventory-transfers/items?${params}`);
        if (cancelled) return;
        setInvItemOptions(data?.items || []);
        setInvItemPage(data?.page ?? 1);
        setInvItemTotal(data?.total ?? 0);
        setInvItemHasMore(Boolean(data?.hasMore));
      } catch {
        if (!cancelled) {
          setInvItemOptions([]);
          setInvItemHasMore(false);
          setInvItemTotal(0);
        }
      } finally {
        if (!cancelled) setInvItemLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [invItemPick.row, invItemPick.q]);

  const loadMoreInvItems = useCallback(async () => {
    if (invItemPick.row == null || invItemLoading || !invItemHasMore || invItemLoadMoreRef.current) return;
    invItemLoadMoreRef.current = true;
    const nextPage = invItemPage + 1;
    setInvItemLoadingMore(true);
    try {
      const params = new URLSearchParams({
        search: invItemPick.q,
        page: String(nextPage),
        pageSize: String(INV_ITEM_PAGE_SIZE),
      });
      const data = await api.get(`/inventory-transfers/items?${params}`);
      const chunk = data?.items || [];
      setInvItemOptions((prev) => [...prev, ...chunk]);
      setInvItemPage(data?.page ?? nextPage);
      setInvItemTotal(data?.total ?? 0);
      setInvItemHasMore(Boolean(data?.hasMore));
    } catch {
      /* keep existing list */
    } finally {
      invItemLoadMoreRef.current = false;
      setInvItemLoadingMore(false);
    }
  }, [invItemPick.row, invItemPick.q, invItemLoading, invItemHasMore, invItemPage]);

  /* ── warehouse picker resolve ── */
  const handleWhSelect = (wh) => {
    if (!whPicker) return;
    const { field } = whPicker;
    if (field === 'from') {
      setHeaderAndSyncLines({
        from_warehouse_id: String(wh.wh_id),
        from_wh_code: wh.wh_code,
      });
    } else if (field === 'to') {
      setHeaderAndSyncLines({
        to_warehouse_id: String(wh.wh_id),
        to_wh_code: wh.wh_code,
      });
    }
    setWhPicker(null);
  };

  /* ── inline item picker (portal) ── */
  const applyItemToTransferLine = (idx, item) => {
    if (!item) return;
    setInvItemLayer(null);
    setInvItemPick({ row: null, q: '' });
    setForm((f) => {
      const h = headerFieldsForLines(f);
      setLines((ls) =>
        ls.map((l, i) =>
          i === idx
            ? {
                ...l,
                item_id: item.item_id,
                item_no: item.item_no,
                item_description: item.description,
                item_cost: item.item_cost ? fmt4(item.item_cost) : '',
                uom_code: item.uom || '',
                uom_name: item.uom || '',
                ...h,
              }
            : l
        )
      );
      return f;
    });
    loadItemWarehouses(item.item_id);
  };

  const clearTransferLineItem = (idx) => {
    setInvItemPick({ row: idx, q: '' });
    setInvItemLayer(null);
    setForm((f) => {
      const h = headerFieldsForLines(f);
      setLines((ls) =>
        ls.map((l, i) =>
          i === idx
            ? {
                ...l,
                item_id: '',
                item_no: '',
                item_description: '',
                item_cost: '',
                uom_code: '',
                uom_name: '',
                ...h,
              }
            : l
        )
      );
      return f;
    });
  };

  /* ── copy from resolve (copies from ITR → IT) ── */
  const handleCopyFrom = async (itrOrList) => {
    setShowCopyFrom(false);
    try {
      const list = Array.isArray(itrOrList) ? itrOrList : [itrOrList];
      if (list.length === 0) return;

      const details = await Promise.all(
        list.map((itr) => api.get(`/inventory-transfer-requests/${itr.request_id}`))
      );

      const base = details[0];
      const hdr = {
        project_code: base.project_code || '',
        project_name: base.project_name || '',
        from_warehouse_id: base.from_warehouse_id ? String(base.from_warehouse_id) : '',
        from_wh_code: base.from_wh_code || '',
        to_warehouse_id: base.to_warehouse_id ? String(base.to_warehouse_id) : '',
        to_wh_code: base.to_wh_code || '',
      };

      // Enforce consistent header fields across selected requests (SAP-style header is single)
      const mismatch = details.find((d) =>
        String(d.from_warehouse_id || '') !== String(base.from_warehouse_id || '') ||
        String(d.to_warehouse_id || '') !== String(base.to_warehouse_id || '') ||
        String(d.project_code || '') !== String(base.project_code || '')
      );
      if (mismatch) {
        setError('Selected requests must have same From Wh, To Wh, and Project to copy into one transfer.');
        return;
      }

      // Combine referenced document: ITR-00001, ITR-00002, ...
      const refDoc = list.map(r => r.request_number).filter(Boolean).join(', ');
      setHeaderAndSyncLines({
        ...hdr,
        referenced_document: refDoc || '',
      });

      // Merge lines by item_id (same warehouses/project implied by hdr)
      const map = new Map(); // key -> merged line
      for (const d of details) {
        for (const l of (d.lines || [])) {
          const itemId = l.item_id || '';
          if (!itemId) continue;
          const key = String(itemId);
          const qty = parseFloat(l.quantity || 0) || 0;
          if (!map.has(key)) {
            map.set(key, {
              ...blankLine(),
              item_id: itemId,
              item_no: l.item_no || '',
              item_description: l.item_description || '',
              project_code: hdr.project_code,
              project_name: hdr.project_name,
              from_warehouse_id: hdr.from_warehouse_id,
              from_wh_code: hdr.from_wh_code,
              to_warehouse_id: hdr.to_warehouse_id,
              to_wh_code: hdr.to_wh_code,
              quantity: fmt4(qty),
              item_cost: '',
              uom_code: l.uom_code || '',
              uom_name: l.uom_name || '',
              product_group: l.product_group || '',
              period: l.period || '',
              requester: l.requester || '',
              request_ref: l.request_ref || '',
              report: l.report || '',
            });
          } else {
            const cur = map.get(key);
            const curQty = parseFloat(cur.quantity || 0) || 0;
            map.set(key, { ...cur, quantity: fmt4(curQty + qty) });
          }
        }
      }
      setLines(Array.from(map.values()));
    } catch {
      setError('Failed to copy from request');
    }
  };

  /* ── save ── */
  const handleSave = async (statusOverride) => {
    const validLines = lines.filter(l => l.item_id);
    setSaving(true); setError(''); setSuccessMsg('');
    try {
      const payload = {
        ...form,
        from_warehouse_id: form.from_warehouse_id || null,
        to_warehouse_id:   form.to_warehouse_id || null,
        status: statusOverride || form.status,
        lines: validLines.map(l => ({
          item_id: l.item_id || null,
          item_no: l.item_no || null,
          item_description: l.item_description || null,
          from_warehouse_id: l.from_warehouse_id || form.from_warehouse_id || null,
          to_warehouse_id:   l.to_warehouse_id || form.to_warehouse_id || null,
          project_code: l.project_code || form.project_code || null,
          project_name: l.project_name || form.project_name || null,
          quantity: parseFloat(l.quantity) || 0,
          item_cost: l.item_cost ? parseFloat(l.item_cost) : null,
          uom_code: l.uom_code || null,
          uom_name: l.uom_name || null,
          product_group: l.product_group || null,
          period: l.period || null,
          requester: l.requester || null,
          request_ref: l.request_ref || null,
          report: l.report || null,
        })),
      };
      if (isNew) {
        if (selectedSeriesId) {
          const consumed = await api.post('/inventory-transfers/consume-number', { series_id: selectedSeriesId });
          if (consumed?.consumed) {
            payload.transfer_number = consumed.consumed;
            payload.series_id = selectedSeriesId;
            payload.series = consumed.series_name;
          }
        }
        const r = await api.post('/inventory-transfers', payload);
        // Build warehouse code lookup
        const whMap = {};
        warehouses.forEach(w => { whMap[String(w.wh_id)] = w.wh_code; });
        const fromWhCode = form.from_wh_code || whMap[String(form.from_warehouse_id)] || form.from_warehouse_id || '—';
        const toWhCode   = form.to_wh_code   || whMap[String(form.to_warehouse_id)]   || form.to_warehouse_id   || '—';
        setSuccessPopup({
          transferNumber: r.transfer_number,
          postingDate: form.posting_date,
          status: statusOverride || form.status,
          fromWh: fromWhCode,
          toWh: toWhCode,
          lines: validLines.map(l => ({
            item_no: l.item_no || '—',
            item_description: l.item_description || '',
            from_wh: l.from_wh_code || whMap[String(l.from_warehouse_id)] || fromWhCode,
            to_wh:   l.to_wh_code   || whMap[String(l.to_warehouse_id)]   || toWhCode,
            quantity: fmt4(l.quantity),
            uom_code: l.uom_code || '',
          })),
        });
      } else {
        await api.put(`/inventory-transfers/${id}`, payload);
        if (statusOverride === 'Open') {
          // Show success popup for Open saves (stock moved)
          const whMap = {};
          warehouses.forEach(w => { whMap[String(w.wh_id)] = w.wh_code; });
          const fromWhCode = form.from_wh_code || whMap[String(form.from_warehouse_id)] || form.from_warehouse_id || '—';
          const toWhCode   = form.to_wh_code   || whMap[String(form.to_warehouse_id)]   || form.to_warehouse_id   || '—';
          setSuccessPopup({
            transferNumber: form.transfer_number,
            postingDate: form.posting_date,
            status: 'Open',
            fromWh: fromWhCode,
            toWh: toWhCode,
            lines: validLines.map(l => ({
              item_no: l.item_no || '—',
              item_description: l.item_description || '',
              from_wh: l.from_wh_code || whMap[String(l.from_warehouse_id)] || fromWhCode,
              to_wh:   l.to_wh_code   || whMap[String(l.to_warehouse_id)]   || toWhCode,
              quantity: fmt4(l.quantity),
              uom_code: l.uom_code || '',
            })),
          });
        } else {
          setSuccessMsg('Saved successfully');
          if (statusOverride) setField('status', statusOverride);
          setTimeout(() => setSuccessMsg(''), 2500);
        }
      }
    } catch (e) {
      setError(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to permanently delete this Inventory Transfer? This will revert any stock movements.')) return;

    setSaving(true);
    setError('');
    setSuccessMsg('');
    try {
      await api.delete(`/inventory-transfers/${id}`);
      setSuccessMsg('Inventory transfer permanently deleted');
      setTimeout(() => navigate('/inventory/transfers'), 1000);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to delete transfer');
      setSaving(false);
    }
  };

  /* ── warehouse picker data ── */
  const getPickerWarehouses = () => {
    if (!whPicker) return [];
    // If there is a line with a selected item, return the item-specific warehouses with stock levels
    const firstLineWithItem = lines.find(l => l.item_id);
    if (firstLineWithItem && itemWarehousesMap[firstLineWithItem.item_id]) {
      return itemWarehousesMap[firstLineWithItem.item_id];
    }
    return warehouses;
  };

  const getPickerCurrentId = () => {
    if (!whPicker) return null;
    if (whPicker.field === 'from') return form.from_warehouse_id;
    if (whPicker.field === 'to') return form.to_warehouse_id;
    return null;
  };

  if (loading) return <div className="py-20 text-center text-xs text-sap-textLight">Loading…</div>;

  const isReadOnly = form.status === 'Completed' || form.status === 'Cancelled';

  /* ════════════════════════════ RENDER ════════════════════════════ */
  return (
    <div className="space-y-2 pb-6">
      {/* Breadcrumb */}
      <div className="text-[10px] text-sap-textLight flex items-center gap-1">
        <button onClick={() => navigate('/inventory/transfers')}
          className="hover:text-sap-blue transition-colors">Inventory Transfer</button>
        <span>›</span>
        <span className="text-sap-text font-medium">{isNew ? 'New' : form.transfer_number}</span>
      </div>

      {/* ── Title bar ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-sm font-bold text-sap-text">Inventory Transfer</h1>
          {!isNew && (
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_COLORS[form.status] || ''}`}>
              {form.status}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {!isNew && (
            <button onClick={handleDelete} disabled={saving}
              className="px-3 py-1 text-xs border border-red-300 text-red-500 rounded-lg hover:bg-red-50 transition-colors font-semibold disabled:opacity-50">
              Delete
            </button>
          )}
          {error    && <span className="text-xs text-red-500">{error}</span>}
          {successMsg && <span className="text-xs text-sap-blueDeeper font-semibold">{successMsg}</span>}
        </div>
      </div>

      {/* ── Document card ── */}
      <div className="bg-white rounded-2xl shadow-sm border border-sap-border/30 overflow-hidden">

        {/* ── Header fields ── */}
        <div className="grid grid-cols-2 gap-0 border-b border-sap-border/30">

          {/* ── Left: Business Partner ── */}
          <div className="p-4 space-y-2 border-r border-sap-border/30">
            <div>
              <label className={lbl}>Business Partner</label>
              <input value={form.bp_code} onChange={e => setField('bp_code', e.target.value)}
                disabled={isReadOnly} placeholder="BP Code" className={inp} />
            </div>
            <div>
              <label className={lbl}>Name</label>
              <input value={form.bp_name} onChange={e => setField('bp_name', e.target.value)}
                disabled={isReadOnly} className={inp} />
            </div>
            <div>
              <label className={lbl}>Contact Person</label>
              <input value={form.contact_person} onChange={e => setField('contact_person', e.target.value)}
                disabled={isReadOnly} className={inp} />
            </div>
            <div>
              <label className={lbl}>Ship To</label>
              <div className="flex items-center gap-1">
                <select value={form.ship_to} onChange={e => setField('ship_to', e.target.value)}
                  disabled={isReadOnly} className={`${sel} flex-1`}>
                  <option value=""></option>
                  {form.ship_to && <option value={form.ship_to}>{form.ship_to}</option>}
                </select>
              </div>
            </div>
            {/* Price List */}
            <div className="pt-1">
              <label className={lbl}>Price List</label>
              <select value={form.price_list} onChange={e => setField('price_list', e.target.value)}
                disabled={isReadOnly} className={sel}>
                {PRICE_LISTS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          {/* ── Right: Document info + Warehouse ── */}
          <div className="p-4 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={lbl}>Number</label>
                <input value={form.transfer_number} readOnly className={`${inp} bg-gray-50 text-sap-textLight`} />
              </div>
              <div>
                <label className={lbl}>Series</label>
                {isNew ? (
                  <div className="relative">
                    <select value={selectedSeriesId || ''}
                      onChange={e => handleSeriesChange(e.target.value)}
                      className={`${sel} pr-6 appearance-none`}>
                      {seriesList.map(s => (
                        <option key={s.series_id} value={s.series_id}>{s.series_name}</option>
                      ))}
                    </select>
                    <svg className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sap-textLight" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                ) : (
                  <input value={form.series || 'Primary'} readOnly className={`${inp} bg-gray-50 text-sap-textLight`} />
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={lbl}>Posting Date</label>
                <input type="date" value={form.posting_date} onChange={e => setField('posting_date', e.target.value)}
                  disabled={isReadOnly} className={inp} />
              </div>
              <div>
                <label className={lbl}>Document Date</label>
                <input type="date" value={form.document_date} onChange={e => setField('document_date', e.target.value)}
                  disabled={isReadOnly} className={inp} />
              </div>
            </div>
            <div>
              <label className={lbl}>Referenced Document</label>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => setShowRefInfo(true)}
                  className="h-[28px] px-2 border border-sap-border rounded text-xs text-sap-textLight hover:bg-gray-50 bg-white">…</button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={lbl}>Project Code</label>
                <div className="flex items-center gap-1">
                  <input
                    value={form.project_code || ''}
                    onChange={(e) => setHeaderAndSyncLines({ project_code: e.target.value })}
                    disabled={isReadOnly}
                    className={`${inp} flex-1 font-mono`}
                    placeholder="Project code"
                  />
                  {!isReadOnly && (
                    <button
                      type="button"
                      onClick={() => setShowProjectPicker(true)}
                      title="Browse projects"
                      className="h-[28px] px-2 border border-sap-border rounded text-xs text-sap-text hover:bg-gray-50 bg-white shrink-0"
                    >
                      …
                    </button>
                  )}
                </div>
              </div>
              <div>
                <label className={lbl}>Project Name</label>
                <input
                  value={form.project_name || ''}
                  readOnly
                  disabled
                  className={dis}
                  placeholder="Pick project or enter code"
                />
              </div>
            </div>

            {/* A transfer is found on the Inventory Transfer Report by its project, so
                one left blank never appears there. Said here, while it can still be
                filled in, rather than left for the report to come back empty. */}
            {!isReadOnly && !String(form.project_code || '').trim() && (
              <div className="text-[11px] text-sap-textLight -mt-1">
                No project code: this transfer will not appear on the Inventory Transfer Report.
              </div>
            )}

            {/* ── From / To Warehouse (with yellow arrows) ── */}
            <div className="pt-1 space-y-1.5 bg-sap-blue/3 rounded-lg p-2 border border-sap-border/40">
              <div className="flex items-center gap-2">
                <span className={`${lbl} w-28 mb-0`}>From Warehouse</span>
                <div className="flex items-center gap-1 flex-1">
                  <YellowArrow title="Choose From Warehouse"
                    onClick={() => setWhPicker({ field: 'from' })} />
                  <input
                    value={form.from_wh_code}
                    onChange={(e) => {
                      const code = e.target.value;
                      const wh = warehouses.find((w) => w.wh_code === code);
                      setHeaderAndSyncLines((f) => ({
                        ...f,
                        from_wh_code: code,
                        from_warehouse_id: wh ? String(wh.wh_id) : '',
                      }));
                    }}
                    disabled={isReadOnly}
                    className={`${inp} w-16 font-mono`}
                    placeholder="01"
                  />
                  <button disabled className="h-[28px] px-1.5 border border-sap-border rounded text-xs text-sap-textLight bg-gray-50 cursor-default">□</button>
                  <span className="text-xs text-sap-textLight truncate max-w-[100px]">
                    {warehouses.find(w => String(w.wh_id) === String(form.from_warehouse_id))?.wh_name || ''}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`${lbl} w-28 mb-0`}>To Warehouse</span>
                <div className="flex items-center gap-1 flex-1">
                  <YellowArrow title="Choose To Warehouse"
                    onClick={() => setWhPicker({ field: 'to' })} />
                  <input
                    value={form.to_wh_code}
                    onChange={(e) => {
                      const code = e.target.value;
                      const wh = warehouses.find((w) => w.wh_code === code);
                      setHeaderAndSyncLines((f) => ({
                        ...f,
                        to_wh_code: code,
                        to_warehouse_id: wh ? String(wh.wh_id) : '',
                      }));
                    }}
                    disabled={isReadOnly}
                    className={`${inp} flex-1 font-mono`}
                    placeholder="01"
                  />
                  <span className="text-xs text-sap-textLight truncate max-w-[120px]">
                    {warehouses.find(w => String(w.wh_id) === String(form.to_warehouse_id))?.wh_name || ''}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`${lbl} w-28 mb-0`}>To Bin Location</span>
                <div className="flex items-center gap-1 flex-1">
                  <input disabled className={`${dis} flex-1`} placeholder="" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="flex border-b border-sap-border/30 bg-[#F7F5F2]">
          {['Contents', 'Attachments'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-5 py-2 text-xs font-medium transition-colors border-b-2
                ${tab === t
                  ? 'border-sap-blue text-sap-blue bg-white'
                  : 'border-transparent text-sap-textLight hover:text-sap-text hover:bg-white/50'}`}>
              {t}
            </button>
          ))}
        </div>

        {/* ══ Contents Tab ══ */}
        {tab === 'Contents' && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-sap-blue/5 border-b border-sap-border/40 text-sap-textLight">
                  <th className="text-center px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-8">#</th>
                  <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-36">Item No.</th>
                  <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider min-w-[160px]">Item Description</th>
                  <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-28">Project Code</th>
                  <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider min-w-[120px]">Project Name</th>
                  <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-28">From Warehouse</th>
                  <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-28 text-sap-textLight/60">From Bin Loc.</th>
                  <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-28">To Warehouse</th>
                  <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-28 text-sap-textLight/60">To Bin Loc.</th>
                  <th className="text-right px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-24">Quantity</th>
                  <th className="text-right px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-24">Item Cost</th>
                  <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-16">UoM Code</th>
                  <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-24">UoM Name</th>
                  <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-24">Product Group</th>
                  <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-20">Period</th>
                  <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-24">Requester</th>
                  <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-24">Request</th>
                  <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-24">Report</th>
                  {!isReadOnly && <th className="w-7"></th>}
                </tr>
              </thead>
              <tbody>
                {lines.map((l, idx) => {
                  const effFrom = l.from_warehouse_id || form.from_warehouse_id;
                  const effFromCode = l.from_wh_code || form.from_wh_code;
                  const effTo = l.to_warehouse_id || form.to_warehouse_id;
                  const effToCode = l.to_wh_code || form.to_wh_code;
                  const fromWhNm = warehouses.find(w => String(w.wh_id) === String(effFrom))?.wh_name || '';
                  const toWhNm = warehouses.find(w => String(w.wh_id) === String(effTo))?.wh_name || '';
                  return (
                    <tr key={l._key || idx}
                      className={`border-t border-sap-border/20 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                      {/* # */}
                      <td className="text-center px-2 py-1 text-sap-textLight font-mono">{idx + 1}</td>
                      {/* Item No. — searchable dropdown via portal (avoids overflow-x-auto clipping) */}
                      <td className="px-1 py-1 align-top">
                        {isReadOnly ? (
                          <span className="text-xs font-mono">{l.item_no || '—'}</span>
                        ) : l.item_id ? (
                          <div className="flex items-center gap-0.5 min-w-[7rem] max-w-[9rem] h-[26px] px-1 border border-sap-border rounded bg-white">
                            <span className="font-mono text-xs text-sap-blue truncate flex-1 min-w-0" title={l.item_no}>{l.item_no}</span>
                            <button
                              type="button"
                              title="Change item"
                              onClick={() => clearTransferLineItem(idx)}
                              className="shrink-0 text-red-400 hover:text-red-600 text-sm leading-none px-0.5">
                              ✕
                            </button>
                          </div>
                        ) : (
                          <div className="relative min-w-[7rem]">
                            <input
                              ref={invItemPick.row === idx ? invItemAnchorRef : undefined}
                              value={invItemPick.row === idx ? invItemPick.q : ''}
                              onChange={(e) => setInvItemPick({ row: idx, q: e.target.value })}
                              onFocus={() => setInvItemPick({ row: idx, q: invItemPick.row === idx ? invItemPick.q : '' })}
                              onBlur={() => {
                                setTimeout(
                                  () => setInvItemPick((p) => (p.row === idx ? { row: null, q: '' } : p)),
                                  200
                                );
                              }}
                              placeholder="Search…"
                              className="w-full h-[26px] px-1 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue font-mono"
                            />
                          </div>
                        )}
                      </td>
                      {/* Item Description */}
                      <td className="px-1 py-1">
                        <input value={l.item_description}
                          onChange={e => updateLine(idx, 'item_description', e.target.value)}
                          disabled={isReadOnly}
                          className="w-full h-[26px] px-1 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue" />
                      </td>
                      {/* Project Code (from header) */}
                      <td className="px-1 py-1">
                        <div className="text-xs font-mono text-sap-text truncate max-w-[7rem]" title={l.project_code || ''}>
                          {l.project_code || '—'}
                        </div>
                      </td>
                      {/* Project Name (from header) */}
                      <td className="px-1 py-1">
                        <div className="text-xs text-sap-text truncate max-w-[10rem]" title={l.project_name || ''}>
                          {l.project_name || '—'}
                        </div>
                      </td>
                      {/* From Warehouse (header; read-only on line) */}
                      <td className="px-1 py-1">
                        <div className="leading-tight">
                          <span className="text-xs font-mono text-sap-text">{effFromCode || '—'}</span>
                          {fromWhNm ? (
                            <span className="block text-[10px] text-sap-textLight truncate max-w-[6rem]" title={fromWhNm}>{fromWhNm}</span>
                          ) : null}
                        </div>
                      </td>
                      {/* From Bin Loc (disabled) */}
                      <td className="px-1 py-1">
                        <input disabled className="w-24 h-[26px] px-1 text-xs border border-sap-border rounded bg-gray-100 text-sap-textLight/60" />
                      </td>
                      {/* To Warehouse (header; read-only on line) */}
                      <td className="px-1 py-1">
                        <div className="leading-tight">
                          <span className="text-xs font-mono text-sap-blue font-semibold">{effToCode || '—'}</span>
                          {toWhNm ? (
                            <span className="block text-[10px] text-sap-textLight truncate max-w-[6rem]" title={toWhNm}>{toWhNm}</span>
                          ) : null}
                        </div>
                      </td>
                      {/* To Bin Loc (disabled) */}
                      <td className="px-1 py-1">
                        <input disabled className="w-24 h-[26px] px-1 text-xs border border-sap-border rounded bg-gray-100 text-sap-textLight/60" />
                      </td>
                      {/* Quantity */}
                      <td className="px-1 py-1">
                        <input type="number" min="0" step="0.0001"
                          value={l.quantity}
                          onChange={e => updateLine(idx, 'quantity', e.target.value)}
                          disabled={isReadOnly}
                          className="w-20 h-[26px] px-1 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue text-right" />
                      </td>
                      {/* Item Cost */}
                      <td className="px-1 py-1">
                        <input type="number" min="0" step="0.0001"
                          value={l.item_cost}
                          onChange={e => updateLine(idx, 'item_cost', e.target.value)}
                          disabled={isReadOnly}
                          className="w-20 h-[26px] px-1 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue text-right" />
                      </td>
                      {/* UoM Code */}
                      <td className="px-1 py-1">
                        <input value={l.uom_code}
                          onChange={e => updateLine(idx, 'uom_code', e.target.value)}
                          disabled={isReadOnly}
                          className="w-14 h-[26px] px-1 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue" />
                      </td>
                      {/* UoM Name */}
                      <td className="px-1 py-1">
                        <input value={l.uom_name}
                          onChange={e => updateLine(idx, 'uom_name', e.target.value)}
                          disabled={isReadOnly}
                          className="w-20 h-[26px] px-1 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue" />
                      </td>
                      {/* Product Group */}
                      <td className="px-1 py-1">
                        <input value={l.product_group} onChange={e => updateLine(idx, 'product_group', e.target.value)}
                          disabled={isReadOnly}
                          className="w-20 h-[26px] px-1 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue" />
                      </td>
                      {/* Period */}
                      <td className="px-1 py-1">
                        <input value={l.period} onChange={e => updateLine(idx, 'period', e.target.value)}
                          disabled={isReadOnly}
                          className="w-16 h-[26px] px-1 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue" />
                      </td>
                      {/* Requester */}
                      <td className="px-1 py-1">
                        <input value={l.requester} onChange={e => updateLine(idx, 'requester', e.target.value)}
                          disabled={isReadOnly}
                          className="w-20 h-[26px] px-1 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue" />
                      </td>
                      {/* Request */}
                      <td className="px-1 py-1">
                        <input value={l.request_ref} onChange={e => updateLine(idx, 'request_ref', e.target.value)}
                          disabled={isReadOnly}
                          className="w-20 h-[26px] px-1 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue" />
                      </td>
                      {/* Report */}
                      <td className="px-1 py-1">
                        <input value={l.report} onChange={e => updateLine(idx, 'report', e.target.value)}
                          disabled={isReadOnly}
                          className="w-20 h-[26px] px-1 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue" />
                      </td>
                      {/* Remove */}
                      {!isReadOnly && (
                        <td className="px-1 py-1 text-center">
                          <button onClick={() => removeLine(idx)}
                            className="text-red-400 hover:text-red-600 text-base leading-none transition-colors">×</button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!isReadOnly && (
              <div className="px-3 py-2 border-t border-sap-border/20">
                <button onClick={addLine}
                  className="text-xs text-sap-blue hover:text-sap-blueDark font-semibold transition-colors">
                  + Add Row
                </button>
              </div>
            )}
          </div>
        )}

        {/* ══ Attachments Tab ══ */}
        {tab === 'Attachments' && (
          <div className="flex gap-0">
            {/* Table area */}
            <div className="flex-1 min-h-[180px] border-r border-sap-border/30">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-[#E8E4DC] border-b border-sap-border/40">
                    <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-8">#</th>
                    <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-48">Target Path</th>
                    <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-48">File Name</th>
                    <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-32">Attachment Date</th>
                    <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider">Free Text</th>
                    <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-32">Copy to Target Document</th>
                  </tr>
                </thead>
                <tbody>
                  {attachments.length === 0 && (
                    <tr><td colSpan={6} className="px-2 py-6 text-center text-sap-textLight">No attachments</td></tr>
                  )}
                  {attachments.map((att, idx) => (
                    <tr key={att.id}
                      onClick={() => setSelectedAttIdx(idx)}
                      className={`cursor-pointer border-b border-sap-border/20 ${
                        selectedAttIdx === idx ? 'bg-blue-50 border-l-2 border-l-sap-blue' : 'hover:bg-gray-50'
                      }`}>
                      <td className="px-2 py-1 text-sap-textLight">{idx + 1}</td>
                      <td className="px-2 py-1 text-sap-textLight truncate max-w-[12rem]" title={att.targetPath}>{att.targetPath}</td>
                      <td className="px-2 py-1 text-sap-blue font-medium truncate max-w-[12rem]" title={att.fileName}>{att.fileName}</td>
                      <td className="px-2 py-1 text-sap-textLight">{att.attachmentDate}</td>
                      <td className="px-2 py-1">
                        <input
                          value={att.freeText}
                          onChange={e => {
                            const updated = [...attachments];
                            updated[idx] = { ...updated[idx], freeText: e.target.value };
                            setAttachments(updated);
                          }}
                          className="w-full h-[22px] px-1 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue"
                        />
                      </td>
                      <td className="px-2 py-1 text-center">
                        <input type="checkbox"
                          checked={att.copyToTarget}
                          onChange={e => {
                            const updated = [...attachments];
                            updated[idx] = { ...updated[idx], copyToTarget: e.target.checked };
                            setAttachments(updated);
                          }}
                          className="accent-sap-blue"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Right-side buttons */}
            <div className="flex flex-col gap-1.5 p-2 bg-[#F2F0EC] border-l border-sap-border/30">
              <input ref={attInputRef} type="file" className="hidden"
                onChange={e => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const today = new Date().toISOString().slice(0, 10);
                  setAttachments(prev => [...prev, {
                    id: Date.now(),
                    targetPath: '',
                    fileName: file.name,
                    attachmentDate: today,
                    freeText: '',
                    copyToTarget: false,
                    file,
                  }]);
                  e.target.value = '';
                }}
              />
              <button
                onClick={() => attInputRef.current?.click()}
                className="px-4 py-1 text-xs border border-sap-border rounded bg-[#E0DDD8] hover:bg-[#d0cdc8] text-sap-text font-semibold w-20 text-center">
                Browse
              </button>
              <button
                disabled={selectedAttIdx === null}
                onClick={() => {
                  if (selectedAttIdx === null) return;
                  const att = attachments[selectedAttIdx];
                  if (att.file) {
                    const url = URL.createObjectURL(att.file);
                    window.open(url, '_blank');
                  }
                }}
                className="px-4 py-1 text-xs border border-sap-border rounded bg-[#E0DDD8] hover:bg-[#d0cdc8] text-sap-text font-semibold w-20 text-center disabled:opacity-50 disabled:cursor-default">
                Display
              </button>
              <button
                disabled={selectedAttIdx === null}
                onClick={() => {
                  if (selectedAttIdx === null) return;
                  const updated = attachments.filter((_, i) => i !== selectedAttIdx);
                  setAttachments(updated);
                  setSelectedAttIdx(null);
                }}
                className="px-4 py-1 text-xs border border-sap-border rounded bg-[#E0DDD8] hover:bg-[#d0cdc8] text-sap-text font-semibold w-20 text-center disabled:opacity-50 disabled:cursor-default">
                Delete
              </button>
            </div>
          </div>
        )}

        {/* ── Footer ── */}
        <div className="grid grid-cols-2 gap-4 p-4 bg-[#F7F5F2] border-t border-sap-border/30">
          <div className="space-y-2">
            <div>
              <label className={lbl}>Sales Employee</label>
              <div className="flex items-center gap-1">
                <input ref={salesEmpRef}
                  value={form.sales_employee} onChange={e => setField('sales_employee', e.target.value)}
                  disabled={isReadOnly}
                  className={`${inp} flex-1 ${salesEmpHighlight ? 'ring-2 ring-sap-blue border-sap-blue' : ''}`}
                  placeholder="-No Sales Employee-" />
                {!isReadOnly && (
                  <button
                    onClick={() => setShowSalesPicker(true)}
                    title="List of Sales Employee"
                    className="flex-shrink-0 h-[28px] px-1.5 border border-sap-border rounded bg-[#E8E4DC] hover:bg-[#d8d4cc] text-sap-text transition-colors flex items-center justify-center">
                    <svg width="14" height="12" viewBox="0 0 14 12" fill="none">
                      <rect x="0" y="1" width="14" height="1.5" rx="0.75" fill="#555"/>
                      <rect x="0" y="5.25" width="14" height="1.5" rx="0.75" fill="#555"/>
                      <rect x="0" y="9.5" width="14" height="1.5" rx="0.75" fill="#555"/>
                    </svg>
                  </button>
                )}
              </div>
            </div>
            <div>
              <label className={lbl}>Journal Remarks</label>
              <input value={form.journal_remarks} onChange={e => setField('journal_remarks', e.target.value)}
                disabled={isReadOnly} className={inp} />
            </div>
          </div>
          <div>
            <label className={lbl}>Remarks</label>
            <textarea value={form.remarks} onChange={e => setField('remarks', e.target.value)}
              disabled={isReadOnly} rows={3}
              className="w-full px-2 py-1 text-xs border border-sap-border rounded bg-white focus:outline-none focus:border-sap-blue resize-none" />
          </div>
        </div>

        {/* ── Action buttons (bottom) ── */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-sap-border/30 bg-white">
          <div className="flex items-center gap-2">
            {!isReadOnly && (
              <>
                <button onClick={() => handleSave('Open')} disabled={saving}
                  className="px-5 py-1.5 text-xs bg-sap-blue text-white rounded-lg hover:bg-sap-blueDark font-semibold transition-colors disabled:opacity-60">
                  {saving ? 'Saving…' : 'Add'}
                </button>
                <button onClick={() => handleSave('Draft')} disabled={saving}
                  className="px-4 py-1.5 text-xs border border-sap-border rounded-lg text-sap-text hover:bg-gray-50 transition-colors disabled:opacity-60">
                  Save Draft
                </button>
              </>
            )}
            <button onClick={() => navigate('/inventory/transfers')}
              className="px-4 py-1.5 text-xs border border-sap-border rounded-lg text-sap-text hover:bg-gray-50 transition-colors">
              Cancel
            </button>
          </div>
          <div className="flex items-center gap-2">
            {!isReadOnly && (
              <div className="flex flex-col items-end gap-0.5">
                <button onClick={() => {
                  setSalesEmpHighlight(true);
                  setCopyFromPending(true);
                  setTimeout(() => salesEmpRef.current?.focus(), 100);
                }}
                  className="px-4 py-1.5 text-xs bg-sap-blue text-white rounded-lg hover:bg-sap-blueDark font-semibold transition-colors">
                  Copy From
                </button>
                {copyFromPending && (
                  <span className="text-[10px] text-sap-blue font-medium border border-sap-blue/40 rounded px-2 py-0.5 bg-sap-blue/5">
                    Inventory Transfer Request
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Warehouse picker dialog ── */}
      {whPicker && (
        <WarehousePickerDialog
          warehouses={getPickerWarehouses()}
          currentWhId={getPickerCurrentId()}
          label={whPicker.field === 'from' ? 'From Warehouse' : 'To Warehouse'}
          onSelect={handleWhSelect}
          onClose={() => setWhPicker(null)}
        />
      )}

      {showProjectPicker && (
        <ProjectPickerDialog
          onSelect={(p) => {
            setHeaderAndSyncLines({
              project_code: p.project_code || '',
              project_name: p.project_name || '',
            });
            setShowProjectPicker(false);
          }}
          onClose={() => setShowProjectPicker(false)}
        />
      )}

      {invItemPick.row != null && invItemLayer && typeof document !== 'undefined' && !isReadOnly &&
        createPortal(
          <div
            role="listbox"
            className="bg-white border border-sap-border rounded-lg shadow-xl overflow-y-auto overflow-x-hidden"
            style={{
              position: 'fixed',
              top: invItemLayer.top,
              left: invItemLayer.left,
              width: invItemLayer.width,
              maxHeight: invItemLayer.maxHeight,
              zIndex: 10050,
            }}
            onMouseDown={(e) => e.preventDefault()}
            onScroll={(e) => {
              const el = e.currentTarget;
              if (invItemLoading || invItemLoadingMore || !invItemHasMore) return;
              if (el.scrollHeight - el.scrollTop - el.clientHeight < 72) loadMoreInvItems();
            }}
          >
            {invItemLoading ? (
              <div className="px-3 py-4 text-xs text-sap-textLight text-center">Searching…</div>
            ) : invItemOptions.length === 0 ? (
              <div className="px-3 py-3 text-xs text-sap-textLight">No matching items</div>
            ) : (
              <>
                {invItemOptions.map((it) => (
                  <button
                    key={`${it.item_id}-${it.item_no}`}
                    type="button"
                    role="option"
                    onClick={() => applyItemToTransferLine(invItemPick.row, it)}
                    className="w-full text-left px-2 py-1.5 text-xs hover:bg-sap-blue/10 border-b border-sap-border/30 last:border-0"
                  >
                    <span className="font-semibold text-sap-blue font-mono">{it.item_no}</span>
                    <span className="ml-1.5 text-sap-textLight block truncate">{it.description}</span>
                  </button>
                ))}
                {invItemHasMore && (
                  <div className="border-t border-sap-border/40 px-2 py-2 flex flex-col gap-1.5">
                    {invItemLoadingMore ? (
                      <div className="text-[10px] text-sap-textLight text-center py-1">Loading more…</div>
                    ) : (
                      <button
                        type="button"
                        className="w-full py-1.5 text-[11px] font-medium text-sap-blue bg-sap-blue/5 rounded border border-sap-blue/25 hover:bg-sap-blue/10"
                        onClick={() => loadMoreInvItems()}
                      >
                        Load more
                        {invItemTotal > 0 ? ` (${invItemOptions.length} of ${invItemTotal})` : ''}
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>,
          document.body
        )}

      {/* ── Copy From dialog ── */}
      {showCopyFrom && (
        <CopyFromDialog
          onSelect={handleCopyFrom}
          onClose={() => setShowCopyFrom(false)}
        />
      )}

      {/* ── Reference Information dialog ── */}
      {showRefInfo && (
        <ReferenceInfoDialog
          refsTo={refsTo} refsBy={refsBy}
          onSave={({ refsTo: t, refsBy: b }) => { setRefsTo(t); setRefsBy(b); }}
          onClose={() => setShowRefInfo(false)}
        />
      )}

      {/* ── Sales Employee picker ── */}
      {showSalesPicker && (
        <SalesEmployeePickerDialog
          current={form.sales_employee}
          onSelect={name => {
            setField('sales_employee', name);
            setSalesEmpHighlight(false);
            if (copyFromPending) {
              setCopyFromPending(false);
              setTimeout(() => setShowCopyFrom(true), 200);
            }
          }}
          onClose={() => {
            setShowSalesPicker(false);
            setSalesEmpHighlight(false);
            setCopyFromPending(false);
          }}
        />
      )}

      {/* ── Success Popup ── */}
      {successPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => {}}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl mx-4 overflow-hidden animate-in fade-in zoom-in">
            {/* Brand header */}
            <div className="bg-gray-50 border-b border-sap-border px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h2 className="text-white text-lg font-bold">Inventory Transfer Created Successfully!</h2>
                <p className="text-sap-textLight text-sm">Transfer #{successPopup.transferNumber} &mdash; {successPopup.postingDate}</p>
              </div>
            </div>

            {/* Transfer details */}
            <div className="px-6 py-4 space-y-4">
              {/* Header info */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Transfer No.</p>
                  <p className="text-sm font-bold text-gray-800">{successPopup.transferNumber}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Posting Date</p>
                  <p className="text-sm font-bold text-gray-800">{successPopup.postingDate}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Status</p>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[successPopup.status] || ''}`}>
                    {successPopup.status}
                  </span>
                </div>
              </div>

              {/* From → To header */}
              <div className="flex items-center gap-3 text-sm">
                <span className="text-gray-500">Header:</span>
                <span className="font-bold text-sap-blueDeeper text-base" title={warehouses.find(w => w.wh_code === successPopup.fromWh)?.wh_name || ''}>
                  {successPopup.fromWh}
                  {(() => {
                    const w = warehouses.find(wh => wh.wh_code === successPopup.fromWh);
                    return w?.wh_name ? ` · ${w.wh_name}` : '';
                  })()}
                </span>
                <span className="text-gray-400">&rarr;</span>
                <span className="font-bold text-sap-blueDeeper text-base" title={warehouses.find(w => w.wh_code === successPopup.toWh)?.wh_name || ''}>
                  {successPopup.toWh}
                  {(() => {
                    const w = warehouses.find(wh => wh.wh_code === successPopup.toWh);
                    return w?.wh_name ? ` · ${w.wh_name}` : '';
                  })()}
                </span>
              </div>

              {/* Transfer lines table */}
              <div>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">Transfer Lines</p>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 text-gray-500">
                        <th className="px-3 py-2 text-left font-semibold w-8">#</th>
                        <th className="px-3 py-2 text-left font-semibold">Item No.</th>
                        <th className="px-3 py-2 text-left font-semibold">Description</th>
                        <th className="px-3 py-2 text-center font-semibold">From</th>
                        <th className="px-3 py-2 text-center font-semibold w-6">&rarr;</th>
                        <th className="px-3 py-2 text-center font-semibold">To</th>
                        <th className="px-3 py-2 text-right font-semibold">Qty</th>
                        <th className="px-3 py-2 text-left font-semibold">UOM</th>
                      </tr>
                    </thead>
                    <tbody>
                      {successPopup.lines.map((ln, i) => (
                        <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                          <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                          <td className="px-3 py-2 font-semibold text-sap-blue">{ln.item_no}</td>
                          <td className="px-3 py-2 text-gray-600">{ln.item_description}</td>
                          <td className="px-3 py-2 text-center font-bold text-sap-blueDeeper" title={warehouses.find(w => w.wh_code === ln.from_wh)?.wh_name || ''}>{ln.from_wh}</td>
                          <td className="px-3 py-2 text-center text-gray-400">&rarr;</td>
                          <td className="px-3 py-2 text-center font-bold text-sap-blueDeeper" title={warehouses.find(w => w.wh_code === ln.to_wh)?.wh_name || ''}>{ln.to_wh}</td>
                          <td className="px-3 py-2 text-right font-medium">{ln.quantity}</td>
                          <td className="px-3 py-2 text-gray-500">{ln.uom_code}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-400 mt-2">{successPopup.lines.length} item{successPopup.lines.length !== 1 ? 's' : ''} transferred</p>
              </div>
            </div>

            {/* OK button */}
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
              <button
                onClick={() => {
                  setSuccessPopup(null);
                  navigate('/inventory/transfers');
                }}
                className="px-8 py-2 bg-[#1B2A4A] text-sap-text text-sm font-semibold rounded-lg hover:bg-[#253658] transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
